import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarCheck2, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { prospectDate, formatProspectTime } from '@/utils/prospectTime';
import { Button } from '@/components/ui/button';

export const ConfirmWhatsAppTime = ({ p, onConfirmed }) => {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const [result, setResult] = useState(null); const pending = useRef(false);
  const pref = p.contact_preference;
  const proposed = prospectDate(pref?.availability_at);
  const confirmation = result && result.version === pref?.submitted_at ? result : p.schedule_confirmation;
  const confirmed = confirmation?.status === 'confirmed' && confirmation.to === (p.email || '').trim() && proposed?.getTime() === prospectDate(confirmation?.start_at)?.getTime();
  const expired = proposed && proposed.getTime() <= Date.now();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((p.email || '').trim());
  const key = (s) => `prospects.scheduleConfirmation.${s}`;
  if (pref?.channel !== 'whatsapp') return null;
  const confirm = async () => {
    if (pending.current || confirmed || !proposed || expired) return;
    pending.current = true; setBusy(true); setError('');
    try {
      const { data } = await api.post(`/admin/prospects/${p.id}/confirm-whatsapp-time`, { expected_start_at: proposed.toISOString() });
      setResult({ ...data, version: pref.submitted_at });
      toast.success(t(key(data.already_confirmed ? 'alreadyConfirmed' : 'success')));
      onConfirmed?.();
    } catch (e) { const message = e?.response?.data?.detail || t(key('failed')); setError(message); toast.error(message); }
    finally { pending.current = false; setBusy(false); }
  };
  return <div className="mt-3 pt-3 border-t border-emerald-200 space-y-2" data-testid={`whatsapp-time-${p.id}`}>
    {proposed && <p className="text-sm text-slate-800 break-words" data-testid={`whatsapp-time-proposed-${p.id}`}><span className="font-semibold">{t(key('proposed'))}: </span>{formatProspectTime(proposed, i18n.language)} · {t(key('timezone'))}</p>}
    <Button size="sm" onClick={confirm} disabled={busy || confirmed || !proposed || expired || !validEmail || p.status === 'declined'}
      className="bg-emerald-700 hover:bg-emerald-800 text-white h-auto min-h-9 max-w-full whitespace-normal text-left"
      data-testid={`whatsapp-time-confirm-${p.id}`}>
      {busy ? <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" /> : confirmed ? <CheckCircle2 className="h-4 w-4 mr-2 shrink-0" /> : <CalendarCheck2 className="h-4 w-4 mr-2 shrink-0" />}
      {t(key(busy ? 'sending' : confirmed ? 'confirmed' : 'confirm'))}
    </Button>
    {confirmed ? <p className="text-xs text-emerald-800 break-words [overflow-wrap:anywhere]" data-testid={`whatsapp-time-sent-${p.id}`}>{t(key('sentTo'), { email: confirmation.to, when: formatProspectTime(confirmation.confirmed_at, i18n.language) })}</p>
      : <p className="text-xs text-slate-600" data-testid={`whatsapp-time-hint-${p.id}`}>{t(key(!proposed ? 'missingTime' : expired ? 'expired' : !validEmail ? 'missingEmail' : 'awaiting'))}</p>}
    {error && <p role="alert" className="text-xs text-rose-700 break-words" data-testid={`whatsapp-time-error-${p.id}`}>{error}</p>}
  </div>;
};